// Native scrolling, same approach as the push cart multibuy carousel: the
// viewport is a normal overflow-x scroller (browser momentum for touch and
// trackpad, no snapping), with mouse drag added on desktop. The arrows step
// one card at a time.
const DRAG_THRESHOLD = 5
const DESKTOP_QUERY = '(min-width: 768px)'

// Click-and-drag scrolling for mouse users. Touch and trackpad already scroll natively.
function enableMouseDrag (scroller, { onStart, onEnd, ignore } = {}) {
  let drag = null
  let suppressClick = false

  const onMouseMove = (event) => {
    if (!drag) return

    // The button was released somewhere we didn't see (e.g. outside the window)
    if (event.buttons === 0) {
      onMouseUp()
      return
    }

    const distance = event.pageX - drag.startX

    if (!drag.dragging && Math.abs(distance) > DRAG_THRESHOLD) {
      drag.dragging = true
      scroller.classList.add('is-dragging')
      onStart?.()
    }

    if (!drag.dragging) return

    event.preventDefault()
    scroller.scrollLeft = drag.scrollLeft - distance
  }

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)

    if (drag?.dragging) {
      suppressClick = true
      scroller.classList.remove('is-dragging')
      onEnd?.()
    }

    drag = null
  }

  // Pressing on an image starts the browser's own "drag this picture" mode,
  // which swallows the mouse release and leaves our drag stuck on
  scroller.addEventListener('dragstart', (event) => event.preventDefault())

  scroller.addEventListener('mousedown', (event) => {
    if (event.button !== 0 || !window.matchMedia(DESKTOP_QUERY).matches) return
    if (ignore?.(event.target)) return

    drag = { startX: event.pageX, scrollLeft: scroller.scrollLeft, dragging: false }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  })

  // A drag that ends over a button or link must not also click it
  scroller.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return
      suppressClick = false
      event.preventDefault()
      event.stopPropagation()
    },
    true,
  )
}

if (!customElements.get('upsell-carousel')) {
  customElements.define(
    'upsell-carousel',
    class UpsellCarousel extends window.BAO.CustomElement {
      constructor() {
        super()

        this.frame = 0
        this.onAfterCartAdd = this.onAfterCartAdd.bind(this)
        this.updateState = this.updateState.bind(this)
        this.onScroll = this.onScroll.bind(this)

        this.setupAtcButtons()
        this.setupCarousel()
      }

      setupListeners() {
        super.setupListeners()

        window.BAO.eventBus.addEventListener(
          window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
          this.onAfterCartAdd,
        )

        window.addEventListener('resize', this.updateState)
      }

      disconnectedCallback() {
        super.disconnectedCallback?.()

        window.BAO.eventBus.removeEventListener(
          window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
          this.onAfterCartAdd,
        )

        window.removeEventListener('resize', this.updateState)
        window.cancelAnimationFrame(this.frame)
      }

      get viewport() {
        return this.querySelector('.upsell-Carousel_Viewport')
      }

      get slides() {
        return [...this.querySelectorAll('[data-upsell-carousel-el="slide"]')]
      }

      setupAtcButtons() {
        this.els.atc?.elements?.forEach((button) => {
          button.addEventListener('click', () => {
            button.closest('.upsell-Carousel_Form')?.classList.add('is-selecting')
          })
        })
      }

      setupCarousel() {
        const viewport = this.viewport
        if (!viewport) return

        if (!this.bound) {
          this.bound = true

          viewport.addEventListener('scroll', this.onScroll, { passive: true })

          enableMouseDrag(viewport, {
            // Size buttons scroll their own strip
            ignore: (target) => target.closest('.upsell-Carousel_Sizes'),
          })

          this.querySelectorAll('.upsell-Carousel_Sizes').forEach((sizes) => {
            enableMouseDrag(sizes)
          })

          this.els.prev?.elements?.forEach((prev) => {
            prev.addEventListener('click', () =>
              this.scrollToSlide(this.nearestIndex() - 1),
            )
          })

          this.els.next?.elements?.forEach((next) => {
            next.addEventListener('click', () =>
              this.scrollToSlide(this.nearestIndex() + 1),
            )
          })
        }

        this.updateState()
      }

      // Left edge of a card, in the viewport's scroll coordinates
      slideOffset(slide) {
        const viewport = this.viewport
        return (
          slide.getBoundingClientRect().left -
          viewport.getBoundingClientRect().left +
          viewport.scrollLeft
        )
      }

      nearestIndex() {
        const left = this.viewport.scrollLeft
        let nearest = 0
        let smallest = Infinity

        this.slides.forEach((slide, index) => {
          const distance = Math.abs(this.slideOffset(slide) - left)
          if (distance < smallest) {
            smallest = distance
            nearest = index
          }
        })

        return nearest
      }

      scrollToSlide(index) {
        const slides = this.slides
        if (!slides.length) return

        const target = slides[Math.min(Math.max(index, 0), slides.length - 1)]
        this.viewport.scrollTo({
          left: this.slideOffset(target),
          behavior: 'smooth',
        })
      }

      onScroll() {
        window.cancelAnimationFrame(this.frame)
        this.frame = window.requestAnimationFrame(this.updateState)
      }

      updateState() {
        const viewport = this.viewport
        if (!viewport) return

        const max = viewport.scrollWidth - viewport.clientWidth
        const atStart = viewport.scrollLeft <= 1
        const atEnd = viewport.scrollLeft >= max - 1

        this.els.prev?.elements?.forEach((prev) => {
          prev.toggleAttribute('disabled', atStart)
          prev.setAttribute('aria-disabled', String(atStart))
        })
        this.els.next?.elements?.forEach((next) => {
          next.toggleAttribute('disabled', atEnd)
          next.setAttribute('aria-disabled', String(atEnd))
        })

        const viewLeft = viewport.getBoundingClientRect().left
        const viewRight = viewLeft + viewport.clientWidth

        this.slides.forEach((slide) => {
          const rect = slide.getBoundingClientRect()
          const visible = rect.left < viewRight - 1 && rect.right > viewLeft + 1
          slide.setAttribute('aria-hidden', String(!visible))
        })
      }

      async onAfterCartAdd(event) {
        if (event.detail?.error) return

        const cartProductIds = new Set()
        const cartVariantIds = new Set()
        const state = event.detail?.state
        const addedItem = event.detail?.item

        if (state?.product_id) cartProductIds.add(String(state.product_id))
        if (state?.id) cartVariantIds.add(String(state.id))
        if (addedItem?.id) cartVariantIds.add(String(addedItem.id))

        if (Array.isArray(state?.items)) {
          state.items.forEach((item) => {
            if (item.product_id) cartProductIds.add(String(item.product_id))
            if (item.id) cartVariantIds.add(String(item.id))
          })
        }

        try {
          const cart = await fetch('/cart.js').then((response) => response.json())
          cart.items.forEach((item) => {
            if (item.product_id) cartProductIds.add(String(item.product_id))
            if (item.id) cartVariantIds.add(String(item.id))
          })
        } catch (error) {
          console.error(error)
        }

        let removed = false

        this.slides.forEach((slide) => {
          const variantIds = (slide.dataset.variantIds || '').split(',')
          const inCart =
            cartProductIds.has(slide.dataset.productId) ||
            variantIds.some((id) => cartVariantIds.has(id))

          if (inCart) {
            slide.remove()
            removed = true
          }
        })

        if (!removed) return

        this.els = this.getElements()

        if (this.slides.length === 0) {
          this.remove()
          return
        }

        this.updateState()
      }
    },
  )
}
